import React, { memo, useEffect, useState } from "react";
import {
  Accordion,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import CurrencyInput from "react-currency-input";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import ptBR from "date-fns/locale/pt-BR";

import { Creators as NotificationsActions } from "../../store/ducks/notifications";

import { isScopes } from "../../services/Auth";
import { maskCPF } from "../../utils/CPF";
import { maskCNPJ } from "../../utils/CNPJ";
import { jsonToForm, formToJson } from "../../utils/Supply";
import { isEmpty } from "../../utils/String";
import { isValidForm } from "../../utils/Form";
import { clearObject, mergeObject } from "../../utils/Object";
import DateUtil from "../../utils/Date";

import ClientService from "../../services/Client";
import ClientSchemaReduced from "../../services/Client/SchemaReduced";
import PatrimonyService from "../../services/Patrimony";
import PatrimonySchemaReduced from "../../services/Patrimony/SchemaReduced";
import SupplyService from "../../services/Supply";
import SupplySchema from "../../services/Supply/Schema";

const ACTIVE_KEY_DETAIL_DATA = "1";
const ACTIVE_KEY_GENERAL_DATA = "0";

registerLocale("pt-BR", ptBR);

const Supply = ({
  options,
  props: { currentUser },
  funcs: { addNotice }
}) => {
  const { t } = useTranslation();
  const clientService = new ClientService();
  const patrimonyService = new PatrimonyService();
  const supplyService = new SupplyService();

  const {
    supply: __supply = {},
    close: __close = () => {}
  } = options;

  const [supply, setSupply] = useState(jsonToForm(mergeObject(JSON.parse(JSON.stringify(SupplySchema)), __supply)));

  useEffect(() => {
    setTimeout(() => {
      if(!isScopes({ currentUser, scopes: [
        "is:master",
        "read:supplys:all"
      ], every: false })) {
        setClientSelectedOption({
          dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client),
          label: currentUser.client.type === "LEG" ? (`${currentUser.client.legal.socialReason} | CNPJ: ${maskCNPJ(currentUser.client.legal.cnpj)}`) : (`${currentUser.client.personal.name} | CPF: ${maskCPF(currentUser.client.personal.cpf)}`),
          value: currentUser.client.id
        });
        setSupply(prevState => ({
          ...prevState,
          client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client)
        }));
      }
    }, 1000);
  }, []); /* eslint-disable-line */

  const [activeKey, setActiveKey] = useState(ACTIVE_KEY_GENERAL_DATA);

  const [activeOptions] = useState([
    { value: true, label: t("Active") },
    { value: false, label: t("Inactive") }
  ]);

  const [clientSelectedOption, setClientSelectedOption] = useState(supply.client.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), supply.client),
    label: supply.client.type === "LEG" ? (`${supply.client.legal.socialReason} | CNPJ: ${maskCNPJ(supply.client.legal.cnpj)}`) : (`${supply.client.personal.name} | CPF: ${maskCPF(supply.client.personal.cpf)}`),
    value: supply.client.id
  });

  const [fuelOptions] = useState([
    { value: "ARL", label: t("Title.Fuel.ARL") },
    { value: "DIE", label: t("Title.Fuel.DIE") },
    { value: "GAS", label: t("Title.Fuel.GAS") }
  ]);

  const [patrimonySelectedOption, setPatrimonySelectedOption] = useState(supply.patrimony.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(PatrimonySchemaReduced)), supply.patrimony),
    label: supply.patrimony.description,
    value: supply.patrimony.id
  });

  const [running, setRunning] = useState(false);

  const buttonDisabled = () => {
    if (!running) return false;
    return true;
  };

  const clientLoadOptions = (e, c) => {
    clientLoadOptionsDebounce(e, c);
  };

  const clientLoadOptionsDebounce = debounce(async (e, c) => {
    const clients = await clientService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterClient(clients));
  }, 1000);

  const changeActiveKey = (e) => {
    if (activeKey === e) return setActiveKey("-1");
    setActiveKey(e);
  };

  const filterClient = (clients) => {
    return clients.map(e => {
      return {
        dataAux: e,
        label: e.type === "LEG" ? (`${e.legal.socialReason} | CNPJ: ${maskCNPJ(e.legal.cnpj)}`) : `${e.personal.name} | CPF: ${maskCPF(e.personal.cpf)}`,
        value: e.id
      };
    });
  };

  const filterPatrimony = (patrimonies) => {
    return patrimonies.map(e => {
      return {
        dataAux: e,
        label: e.description,
        value: e.id
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!running) {
      setRunning(true);
      let { eventKey, message, valid } = isValidForm(e);
      if (!valid) {
        if (!message) {
          message = t("Error.Fields.Empty");
        }
        if (eventKey !== -1) {
          setActiveKey(eventKey);
        }
        addNotice({
          title: t("Title.Supply"),
          content: message
        });
      }
      else {
        let response = false;
        if (isCreate()) {
          response = await supplyService.create({
            data: handleSubmitForm({ supply }),
            token: currentUser.token
          });
        }
        else {
          response = await supplyService.update({
            id: supply.id,
            data: handleSubmitForm({ supply }),
            token: currentUser.token
          });
        }

        if (response.success) {
          addNotice({
            title: t("Title.Supply"),
            content: isCreate() ? t("Success.Insert") : t("Success.Update")
          });
          setTimeout(() => {
            __close();
          }, 200);
        }
        else {
          addNotice({
            title: t("Title.Supply"),
            content: t(`Error.Supply.${response.error.type}`)
          });
        }
      }
    }
    setRunning(false);
  };

  const handleSubmitForm = ({ supply }) => {
    let newSupply = Object.assign({}, JSON.parse(JSON.stringify(supply)));
    newSupply = clearObject({ data: newSupply });
    newSupply.id = undefined;
    return formToJson(newSupply);
  };

  const inputDisabled = () => {
    if (!running) return false;
    return true;
  };

  const isCreate = () => {
    return supply.id === "";
  };

  const patrimonyLoadOptions = (e, c) => {
    patrimonyLoadOptionsDebounce(e, c);
  };

  const patrimonyLoadOptionsDebounce = debounce(async (e, c) => {
    const patrimonies = await patrimonyService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterPatrimony(patrimonies));
  }, 1000);

  return (
    <Form id="forms-supply" noValidate onSubmit={handleSubmit}>
      <Accordion defaultActiveKey={ACTIVE_KEY_GENERAL_DATA} activeKey={activeKey}>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_GENERAL_DATA} onClick={e => changeActiveKey(ACTIVE_KEY_GENERAL_DATA)}>{t("Title.GeneralData")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_GENERAL_DATA}>
            <Card.Body>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-supply-active">
                    <Form.Label>{t("Label.Active")}:</Form.Label>
                    <Select
                      className="menu-outer-bottom"
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      isDisabled={inputDisabled()}
                      onChange={e => {
                        const active = e.value;
                        setSupply(prevState => ({
                          ...prevState,
                          active
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      options={activeOptions}
                      value={activeOptions.find(x => x.value === supply.active)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-supply-client">
                    <Form.Label>{t("Title.Client")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(supply.client.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled() || !isScopes({
                        currentUser,
                        scopes: [
                          "is:master",
                          "read:supplys:all"
                        ],
                        every: false
                      })}
                      loadOptions={clientLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if(e === null) {
                          setClientSelectedOption(e);
                          setSupply(prevState => ({
                            ...prevState,
                            client: JSON.parse(JSON.stringify(ClientSchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setClientSelectedOption({ dataAux, label, value });
                          setSupply(prevState => ({
                            ...prevState,
                            client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      placeholder={t("Title.Client.Select")}
                      value={clientSelectedOption}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(supply.client.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Client")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Form.Group controlId="forms-supply-patrimony">
                    <Form.Label>{t("Title.Patrimony")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(supply.patrimony.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled()}
                      loadOptions={patrimonyLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if(e === null) {
                          setPatrimonySelectedOption(e);
                          setSupply(prevState => ({
                            ...prevState,
                            patrimony: JSON.parse(JSON.stringify(PatrimonySchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setPatrimonySelectedOption({ dataAux, label, value });
                          setSupply(prevState => ({
                            ...prevState,
                            patrimony: mergeObject(JSON.parse(JSON.stringify(PatrimonySchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      placeholder={t("Title.Patrimony.Select")}
                      value={patrimonySelectedOption}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(supply.patrimony.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Patrimony")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_DETAIL_DATA} onClick={e => changeActiveKey(ACTIVE_KEY_DETAIL_DATA)}>{t("Title.DetailData")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_DETAIL_DATA}>
            <Card.Body>
              <Row>
                <Col xs={12}>
                  <div className="filter-date-fields">
                    <Form.Group controlId="forms-supply-date">
                      <Form.Label>{t("Title.Date")}:</Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                          <InputGroup.Text><i className="fas fa-calendar-minus" /></InputGroup.Text>
                        </InputGroup.Prepend>
                        <DatePicker
                          disabled={inputDisabled()}
                          selected={DateUtil.toDate({ date: supply.date })}
                          onChange={dateStart => {
                            const todayDate = `${(new Date()).getFullYear()}-${(new Date()).getMonth()+1}-${(new Date()).getDate()}`;
                            const date = dateStart === null ? new Date(`${todayDate} 00:00:00`) : dateStart;
                            setSupply(prevState => ({
                              ...prevState,
                              date
                            }));
                          }}
                          selectsStart
                          startDate={DateUtil.toDate({ date: supply.date })}
                          timeFormat="HH:mm"
                          timeIntervals={5}
                          showTimeSelect
                          showMonthDropdown
                          showYearDropdown
                          dateFormat="dd/MM/yyyy HH:mm:ss"
                          className="react-datepicker-form-control"
                          id="forms-supply-date"
                          popperPlacement="top-center"
                          popperModifiers={{
                            offset: {
                              enabled: true,
                              offset: "5px, 10px"
                            },
                            preventOverflow: {
                              enabled: true,
                              escapeWithReference: false,
                              boundariesElement: "viewport"
                            }
                          }}
                          locale="pt-BR"
                          dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                        />
                      </InputGroup>
                    </Form.Group>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-supply-liters">
                    <Form.Label>{t("Label.Liters")}:</Form.Label>
                    <CurrencyInput
                      id="forms-supply-liters"
                      className={`form-control ${!isEmpty(supply.liters) ? "is-valid" : "is-invalid"}`}
                      type="text"
                      placeholder={t("Placeholder.Liters")}
                      disabled={inputDisabled()}
                      decimalSeparator=","
                      thousandSeparator="."
                      precision="3"
                      prefix=""
                      suffix="L"
                      value={supply.liters}
                      onChangeEvent={e => {
                        const liters = e.target.value;
                        setSupply(prevState => ({
                          ...prevState,
                          liters
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Liters")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-supply-price">
                    <Form.Label>{t("Label.Price")}:</Form.Label>
                    <CurrencyInput
                      id="forms-supply-price"
                      className={`form-control ${!isEmpty(supply.price) ? "is-valid" : "is-invalid"}`}
                      type="text"
                      placeholder={t("Placeholder.Price")}
                      disabled={inputDisabled()}
                      decimalSeparator=","
                      thousandSeparator="."
                      precision="2"
                      prefix="R$ "
                      value={supply.price}
                      onChangeEvent={e => {
                        const price = e.target.value;
                        setSupply(prevState => ({
                          ...prevState,
                          price
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Price")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-supply-hodometer">
                    <Form.Label>{t("Label.Hodometer")}:</Form.Label>
                    <CurrencyInput
                      id="forms-supply-hodometer"
                      className={`form-control ${!isEmpty(supply.hodometer) ? "is-valid" : "is-invalid"}`}
                      type="text"
                      placeholder={t("Placeholder.Hodometer")}
                      disabled={inputDisabled()}
                      decimalSeparator="."
                      thousandSeparator=""
                      precision="0"
                      prefix=""
                      value={supply.hodometer}
                      onChangeEvent={e => {
                        const hodometer = e.target.value;
                        setSupply(prevState => ({
                          ...prevState,
                          hodometer
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Hodometer")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-supply-fuel">
                    <Form.Label>{t("Label.Fuel")}:</Form.Label>
                    <Select
                      className="menu-outer-bottom"
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      isDisabled={inputDisabled()}
                      onChange={e => {
                        const fuel = e.value;
                        setSupply(prevState => ({
                          ...prevState,
                          fuel
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      options={fuelOptions}
                      value={fuelOptions.find(x => x.value === supply.fuel)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-supply-address">
                    <Form.Label>{t("Label.Address")}:</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder={t("Label.Address")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const address = e.target.value;
                        setSupply(prevState => ({
                          ...prevState,
                          address
                        }));
                      }}
                      autoComplete="off"
                      value={supply.address}
                      isValid={!isEmpty(supply.address)}
                      isInvalid={isEmpty(supply.address)}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Address")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-supply-description">
                    <Form.Label>{t("Label.Description")}:</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder={t("Label.Description")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const description = e.target.value;
                        setSupply(prevState => ({
                          ...prevState,
                          description
                        }));
                      }}
                      autoComplete="off"
                      value={supply.description}
                      isValid={!isEmpty(supply.description)}
                      isInvalid={isEmpty(supply.description)}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Description")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <Form.Group className="default-form-button">
        <Button
          variant="dark"
          type="submit"
          disabled={buttonDisabled()}
        >
          {running ? <Spinner animation="border" size="sm" /> : t("Button.Save")}
        </Button>
      </Form.Group>
    </Form>
  );
};

const mapStateToProps = state => ({
  props: {
    currentUser: state.users.currentUser
  }
});

const mapDispatchToProps = dispatch => ({
  funcs: bindActionCreators(Object.assign({}, NotificationsActions), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(Supply));
